<template>
	<Modal
		:show="isOpen"
		@close="closeModal"
		@click:backdrop="closeModal"
		:close-button-visibility="true"
		alignment="right"
		animation="slide-right"
	>
		<template #body>
			<div class="language-modal__content">
				<h3 class="language-modal__title">{{ $t('common.language_selector.title') }}</h3>

				<div class="language-modal__languages-list">
					<div
						v-for="lang in availableLanguages"
						:key="lang.code"
						class="language-modal__language-option"
						:class="{
							'language-modal__language-option_selected': isLanguageSelected(lang.code),
							'language-modal__language-option_pending': isPendingLanguage(lang.code),
						}"
						@click="selectLanguage(lang.code)"
					>
						<div class="language-modal__language-info">
							<span class="language-modal__language-name">{{ lang.name }}</span>
							<span class="language-modal__language-code">({{ getShortLanguageCode(lang.code) }})</span>
						</div>

						<!-- Check Icon -->
						<svg
							class="language-modal__check-icon"
							:class="{
								'language-modal__check-icon_visible':
									isPendingLanguage(lang.code) || (!pendingLanguage && isLanguageSelected(lang.code)),
							}"
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								fill-rule="evenodd"
								clip-rule="evenodd"
								d="M20.9887 5.23293C21.4732 5.68514 21.4993 6.44449 21.0471 6.92898L10.2089 18.3978C9.34247 19.3075 7.87258 19.3243 7.00175 18.388L7.00054 18.3867L2.95255 14.0489C2.50039 13.5643 2.52664 12.805 3.01118 12.3528C3.49572 11.9007 4.25506 11.9269 4.70722 12.4115L8.60991 16.5936L19.2927 5.29135C19.7449 4.80686 20.5042 4.78071 20.9887 5.23293Z"
								fill="var(--Fill-Accent-100)"
							/>
						</svg>
					</div>
				</div>
			</div>
		</template>
	</Modal>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import Modal from '@/components/Modal.vue'
import Jet from '@/modules/Jet'
import { SUPPORTED_LANGUAGES } from '@/helpers/constants'

// Language mappings for display (can include extended locale codes)
const LANGUAGE_MAP = {
	en: 'English',
	de: 'Deutsch',
	'de-DE': 'Deutsch',
	fr: 'Français',
	'fr-FR': 'Français',
	tr: 'Türkçe',
	sv: 'Svenska',
	pl: 'Polski',
	pt: 'Português',
	'pt-BR': 'Português',
	it: 'Italiano',
	'it-IT': 'Italiano',
	es: 'Español',
	'es-ES': 'Español',
	'es-419': 'Español',
	da: 'Dansk',
	nb: 'Norsk',
	no: 'Norsk',
	nl: 'Nederlands',
}

// Multilingual countries with language priority order (only codes from SupportedLanguages)
// Note: Language switching always normalizes to SupportedLanguages format
const MULTILINGUAL_COUNTRIES = {
	CA: ['en', 'fr'], // Canada: English, French
	US: ['en', 'es', 'es-419'], // USA: English, Spanish (Latin American)
	CH: ['de', 'fr', 'it'], // Switzerland: German, French, Italian
	BE: ['nl', 'fr', 'de'], // Belgium: Dutch, French, German
	LU: ['fr', 'de'], // Luxembourg: French, German
	FI: ['sv'], // Finland: Swedish (Finnish not available)
	NO: ['nb'], // Norway: Norwegian
	PT: ['pt'], // Portugal: Portuguese
	BR: ['pt'], // Brazil: Portuguese
	ES: ['es'], // Spain: Spanish
	MX: ['es-419', 'es'], // Mexico: Spanish (Latin American)
}

// Language normalization map for common locale codes
const LANG_NORMALIZE_MAP = {
	'de-DE': 'de',
	'fr-FR': 'fr',
	'es-ES': 'es',
	'pt-BR': 'pt',
	'it-IT': 'it',
	no: 'nb',
}

export default {
	name: 'LanguageModal',
	components: {
		Modal,
	},

	data() {
		return {
			pendingLanguage: null, // Язык, который пользователь выбрал и который переходит в состояние "выбран"
		}
	},

	computed: {
		...mapGetters({
			language: 'i18n/getLanguage',
			getQueryParam: 'location/getQueryParam',
			appAreaConfig: 'location/getAppAreaConfig',
			userCountry: 'getCountryCodeUppercase',
			getLanguageModalState: 'getLanguageModalState',
		}),

		isOpen() {
			return this.getLanguageModalState.showModal
		},

		screenName() {
			return this.getLanguageModalState.screenName || 'unknown'
		},

		currentLanguage() {
			return this.getQueryParam('lang') || this.language || 'en'
		},

		availableLanguages() {
			// Get available languages from current funnel/appArea
			const funnelLocales = this.getFunnelLocales()
			const languages = funnelLocales.map((code) => ({
				code: code,
				name: LANGUAGE_MAP[code] || code.toUpperCase(),
				normalizedCode: this.normalizeLangCode(code),
			}))

			return this.applyCountryPriorities(languages)
		},
	},

	methods: {
		...mapMutations({
			setLanguageModalState: 'setLanguageModalState',
		}),

		open(screenName = 'unknown') {
			this.setLanguageModalState({ showModal: true, screenName })
		},

		closeModal() {
			// Send OnboardingLanguageSelectionDone event when modal closes
			if (this.isOpen) {
				this.$analytic.logEvent('OnboardingLanguageSelectionDone', {
					initialLanguage: this.currentLanguage,
					chosenLanguage: this.currentLanguage, // User closed without selecting, so chosen = current
					screen: this.screenName,
				})
				Jet.logLogicalEvent('OnboardingLanguageSelectionDone', {
					initialLanguage: this.currentLanguage,
					chosenLanguage: this.currentLanguage, // User closed without selecting, so chosen = current
					screen: this.screenName,
				})
			}

			this.pendingLanguage = null // Сбросить pending состояние при закрытии модалки
			this.setLanguageModalState({ showModal: false, screenName: '' })
		},

		isLanguageSelected(langCode) {
			// Compare normalized versions to handle fr-FR vs fr
			const normalizedLangCode = this.normalizeLangCode(langCode)
			const normalizedCurrentLang = this.normalizeLangCode(this.currentLanguage)

			// Если есть pending язык, то текущий язык становится неактивным
			if (this.pendingLanguage) {
				return false
			}

			return normalizedLangCode === normalizedCurrentLang
		},

		isPendingLanguage(langCode) {
			if (!this.pendingLanguage) return false

			const normalizedLangCode = this.normalizeLangCode(langCode)
			const normalizedPendingLang = this.normalizeLangCode(this.pendingLanguage)

			return normalizedLangCode === normalizedPendingLang
		},

		selectLanguage(langCode) {
			// Normalize language code to supported format
			const normalizedLangCode = this.normalizeLangCode(langCode)
			const normalizedCurrentLang = this.normalizeLangCode(this.currentLanguage)

			// Если выбирается уже текущий язык, просто закрыть модалку
			if (normalizedLangCode === normalizedCurrentLang) {
				this.closeModal()
				return
			}

			// Если уже есть pending язык, не обрабатывать новые клики
			if (this.pendingLanguage) {
				return
			}

			// Установить выбранный язык как pending для визуального отображения
			this.pendingLanguage = langCode

			// Send OnboardingLanguageSelectionDone event when user selects language
			this.$analytic.logEvent('OnboardingLanguageSelectionDone', {
				initialLanguage: this.currentLanguage,
				chosenLanguage: normalizedLangCode,
				screen: this.screenName,
			})
			Jet.logLogicalEvent('OnboardingLanguageSelectionDone', {
				initialLanguage: this.currentLanguage,
				chosenLanguage: normalizedLangCode,
				screen: this.screenName,
			})

			// Перезагрузить страницу с новым языком после анимации, чтобы дать время ивенту отправиться
			setTimeout(() => {
				// Add language parameter to current URL and reload
				const url = new URL(window.location)
				url.searchParams.set('lang', normalizedLangCode)
				window.location.href = url.toString()
			}, 350)
		},

		getFunnelLocales() {
			// Get locales from current funnel configuration
			if (this.appAreaConfig?.funnel?.locale && Array.isArray(this.appAreaConfig.funnel.locale)) {
				return this.appAreaConfig.funnel.locale
			}

			return ['en']
		},

		getShortLanguageCode(langCode) {
			// Special case for Latin American Spanish
			if (langCode === 'es-419') {
				return 'LATAM'
			}
			// Extract short code (before dash) and convert to uppercase
			return langCode.split('-')[0].toUpperCase()
		},

		normalizeLangCode(langCode) {
			/**
			 * Normalizes language codes to format from SUPPORTED_LANGUAGES
			 * This ensures the app can properly handle language switching
			 */

			// If code is already in supported languages, return as is
			if (SUPPORTED_LANGUAGES.includes(langCode)) {
				return langCode
			}

			// Check if we have a specific mapping
			if (LANG_NORMALIZE_MAP[langCode]) {
				return LANG_NORMALIZE_MAP[langCode]
			}

			// Try base language code (before dash)
			const baseCode = langCode.split('-')[0]
			if (SUPPORTED_LANGUAGES.includes(baseCode)) {
				return baseCode
			}

			// Default to English if not supported
			return 'en'
		},

		// Get country-specific language priorities (filtered by available languages)
		getCountryPriorities(availableLanguageCodes) {
			const countryPriorities = MULTILINGUAL_COUNTRIES[this.userCountry]

			if (!countryPriorities) return null

			// Фильтруем приоритеты: ищем доступные языки по нормализованным кодам
			const filtered = countryPriorities.filter((priorityCode) => {
				return availableLanguageCodes.some((availableCode) => {
					const normalizedAvailable = this.normalizeLangCode(availableCode)
					const normalizedPriority = this.normalizeLangCode(priorityCode)
					return normalizedAvailable === normalizedPriority
				})
			})

			return filtered.length > 0 ? filtered : null
		},

		// Check if language should be prioritized based on country (compare normalized codes)
		getLanguagePriorityIndex(langCode, countryPriorities) {
			if (!countryPriorities) return -1

			const normalizedLangCode = this.normalizeLangCode(langCode)

			// Ищем индекс по нормализованному коду
			const index = countryPriorities.findIndex((priorityCode) => {
				const normalizedPriorityCode = this.normalizeLangCode(priorityCode)
				return normalizedPriorityCode === normalizedLangCode
			})

			return index
		},

		// Apply country-specific priorities to pre-sorted languages
		applyCountryPriorities(languages) {
			const currentLangNormalized = this.normalizeLangCode(this.currentLanguage)
			const availableLanguageCodes = languages.map((l) => l.code)
			const countryPriorities = this.getCountryPriorities(availableLanguageCodes)

			// Create a copy to avoid mutating original array
			const sortedLanguages = [...languages]

			// Sort logic
			sortedLanguages.sort((a, b) => {
				const aNormalized = this.normalizeLangCode(a.code)
				const bNormalized = this.normalizeLangCode(b.code)

				// 1. Selected language always first (compare normalized versions)
				if (aNormalized === currentLangNormalized) return -1
				if (bNormalized === currentLangNormalized) return 1

				// 2. For multilingual countries: use country-specific order
				if (countryPriorities) {
					const aIndex = this.getLanguagePriorityIndex(a.code, countryPriorities)
					const bIndex = this.getLanguagePriorityIndex(b.code, countryPriorities)

					// Both languages are in country priorities
					if (aIndex !== -1 && bIndex !== -1) {
						return aIndex - bIndex
					}
					// Only 'a' is in priorities - should come first
					if (aIndex !== -1) return -1
					// Only 'b' is in priorities - should come first
					if (bIndex !== -1) return 1
				} else {
					// 3. For other countries: English second (if not selected)
					if (a.code === 'en' && b.code !== 'en') return -1
					if (b.code === 'en' && a.code !== 'en') return 1
				}

				// 4. Alphabetical order for remaining languages
				return a.name.localeCompare(b.name)
			})

			return sortedLanguages
		},
	},

	watch: {
		isOpen(newValue) {
			// Send OnboardingLanguageSelectionView event when modal opens
			if (newValue) {
				this.$analytic.logEvent('OnboardingLanguageSelectionView', {
					initialLanguage: this.currentLanguage,
					screen: this.screenName,
				})
				Jet.logLogicalEvent('OnboardingLanguageSelectionView', {
					initialLanguage: this.currentLanguage,
					screen: this.screenName,
				})
			}
		},
	},

	mounted() {
		// Close modal when clicking outside
		this.handleKeydown = (e) => {
			if (e.key === 'Escape' && this.isOpen) {
				this.closeModal()
			}
		}
		document.addEventListener('keydown', this.handleKeydown)
	},

	beforeDestroy() {
		document.removeEventListener('keydown', this.handleKeydown)
	},
}
</script>

<style scoped lang="scss">
.language-modal {
	&__content {
		display: flex;
		flex-direction: column;
		padding: var(--size-28) var(--size-20);
		gap: var(--size-24);
		background: var(--Fill-Base);
	}

	&__title {
		font-weight: 700;
		font-size: var(--size-24);
		line-height: 1.17;
		color: var(--Text-Primary-100);
		margin: 0;
		padding: 0;
	}

	&__languages-list {
		display: flex;
		flex-direction: column;
		padding: 0;
	}

	&__language-option {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: var(--size-12) var(--size-16);
		height: 48px;
		background: var(--Fill-Base);
		border-radius: var(--size-12);
		cursor: pointer;
		transition: all 0.3s ease;

		&_selected,
		&_pending {
			background: var(--Fill-Accent-Dimmed);
			font-weight: 600;
		}

		&_pending {
			transform: scale(1.02);
			cursor: default;

			.language-modal__check-icon {
				opacity: 1;
			}
		}

		&:hover:not(&_selected):not(&_pending) {
			background: var(--Fill-Default);
		}
	}

	&__language-info {
		display: flex;
		align-items: center;
		gap: var(--size-4);
		font-size: var(--size-16);
		line-height: 1.25;
		color: var(--Text-Primary-100);

		span {
			margin: 0;
		}
	}

	&__check-icon {
		width: var(--size-24);
		height: var(--size-24);
		opacity: 0;
		transition: opacity 0.2s ease;

		&_visible {
			opacity: 1;
		}
	}
}
</style>
